import React from "react";
import { Save, Info } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { FormikInput, FormikSelect } from "@/components/FormikField";
import type { FormikProps } from "formik";
import type { SilaeFormData } from "@/types/silae";
import { useTranslation } from "react-i18next";
import { useVariableNameCollisions } from "@/hooks/useVariableNameCollisions";
import { VariableNameCollisionAlert } from "./VariableNameCollisionAlert";

interface StockageConfigProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix?: string; // Nouveau système: chemin complet (ex: "ifBranch.bonuses.0")
  className?: string;
}

/**
 * Composant réutilisable pour la configuration du stockage
 */
export const StockageConfig: React.FC<StockageConfigProps> = ({
  formik,
  fieldPrefix,
  className = "",
}) => {
  const { t } = useTranslation();

  // Nouveau système avec fieldPrefix (structure hiérarchique)
  const enableField = fieldPrefix
    ? `${fieldPrefix}.enableStockage`
    : "enableStockage";

  const variableNameField = fieldPrefix
    ? `${fieldPrefix}.stockageVariableName`
    : "stockageVariableName";

  const variableTypeField = fieldPrefix
    ? `${fieldPrefix}.stockageVariableType`
    : "stockageVariableType";

  const customExprField = fieldPrefix
    ? `${fieldPrefix}.stockageCustomExpression`
    : "stockageCustomExpression";

  // Helper pour accéder aux valeurs imbriquées
  const getFieldValue = (path: string) => {
    const keys = path.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = formik.values;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return undefined;
    }
    return value;
  };

  const isEnabled = getFieldValue(enableField) as boolean;
  const stockageVariableName = getFieldValue(variableNameField) as string;
  const stockageVariableType = getFieldValue(variableTypeField) as string;
  const collisions = useVariableNameCollisions(formik.values);

  // Options pour le type de variable de stockage
  const variableTypeOptions = [
    {
      value: "bases",
      label: t("storageConfig.variableTypes.bases"),
    },
    {
      value: "tauxs",
      label: t("storageConfig.variableTypes.tauxs"),
    },
    {
      value: "bases_times_tauxs",
      label: t("storageConfig.variableTypes.bases_times_tauxs"),
    },
    {
      value: "custom",
      label: t("storageConfig.variableTypes.custom"),
    },
  ];

  return (
    <Card className={`bg-background mb-0 dark:bg-background ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Save className="h-6 w-6 text-muted-foreground" />
            <div className="flex flex-col ">
              <CardTitle className="text-lg">
                {t("storageConfig.title")}
                <Badge variant="outline" className="text-xs ml-2">
                  {t("storageConfig.optional")}
                </Badge>
              </CardTitle>
              <CardDescription>
                {t("storageConfig.description")}
              </CardDescription>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={(checked) => {
              formik.setFieldValue(enableField, checked);
              if (!checked) {
                formik.setFieldValue(variableNameField, "");
                formik.setFieldValue(variableTypeField, "bases");
                formik.setFieldValue(customExprField, "");
              }
              setTimeout(() => formik.validateForm(), 0);
            }}
          />
        </div>
      </CardHeader>
      {isEnabled && (
        <CardContent className="space-y-4">
          <div className="space-y-4 pt-4 border-t">
            {/* Nom de la variable de stockage */}
            <FormikInput
              formik={formik}
              name={variableNameField}
              label={t("storageConfig.variableName")}
              placeholder={t("storageConfig.variablePlaceholder")}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                formik.setFieldValue(variableNameField, value);
              }}
            />
            <VariableNameCollisionAlert
              name={stockageVariableName}
              duplicates={collisions}
            />

            {/* Type de variable de stockage */}
            <FormikSelect
              formik={formik}
              name={variableTypeField}
              label={t("storageConfig.variableType")}
              placeholder={t("storageConfig.variableType")}
              options={variableTypeOptions}
            />

            {/* Expression personnalisée */}
            {stockageVariableType === "custom" && (
              <FormikInput
                formik={formik}
                name={customExprField}
                label={t("storageConfig.customExpression")}
                placeholder={t("storageConfig.customExpressionPlaceholder")}
              />
            )}

            {/* Information sur l'utilité du stockage */}
            <div className="flex items-start gap-2 p-3 bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-800 rounded-lg">
              <Info className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-sky-700 dark:text-sky-300">
                <p className="mb-1">{t("storageConfig.helpText1")}</p>
                <p className="text-xs">{t("storageConfig.helpText2")}</p>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
